'use client'

import { AchievementFragment, GetAchievementCommunityDocument } from '@/gql/generated/graphql'
import { Button } from '@components/button'
import { Dialog } from '@components/dialog'
import { useQuery } from '@urql/next'
import { ArrowRightIcon, BodyText, DetailText, XIcon } from 'design-configuration/components'
import { useState } from 'react'
import { CommunityUserItem } from './community-user-item'

type AchievementCommunityModalProps = {
  show: boolean
  onDismiss: () => void
  achievement: AchievementFragment
}

const USERS_PER_PAGE = 30

export const AchievementCommunityModal = ({
  show,
  onDismiss,
  achievement
}: AchievementCommunityModalProps): React.ReactElement => {
  const [pageNumber, setPageNumber] = useState(1)

  const [{ data, fetching, error }] = useQuery({
    query: GetAchievementCommunityDocument,
    variables: {
      achievementId: achievement.id,
      limit: USERS_PER_PAGE,
      offset: (pageNumber - 1) * USERS_PER_PAGE
    },
    pause: !show // Only fetch when modal is open
  })

  const userAchievements = data?.user_achievements ?? []
  const totalCount = data?.user_achievements_aggregate?.aggregate?.count ?? 0
  const totalPages = Math.ceil(totalCount / USERS_PER_PAGE)

  const hasNextPage = pageNumber < totalPages
  const hasPrevPage = pageNumber > 1

  const handleNextPage = () => {
    if (hasNextPage) {
      setPageNumber(pageNumber + 1)
    }
  }

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setPageNumber(pageNumber - 1)
    }
  }

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) {
          onDismiss()
        }
      }}
    >
      <Dialog.Overlay />
      <Dialog.Content className="focus-visible:outline-none">
        <div className="flex min-h-[90vh] w-full flex-col lg:min-h-[500px]">
          {/* Header */}
          <div className="p-sm border-b-outline-variant gap-xs flex flex-col border-b">
            <div className="flex w-full items-start justify-between">
              <div className="gap-xs flex flex-col">
                <BodyText className="font-medium">{achievement.name} Community</BodyText>
                {achievement.description ? (
                  <BodyText className="text-on-surface-variant">
                    The following members{' '}
                    {achievement.description.charAt(0).toLowerCase() + achievement.description.slice(1)}
                  </BodyText>
                ) : null}
              </div>
              <button onClick={onDismiss} className="transition-all duration-200">
                <XIcon className="h-sm w-sm fill-on-surface" />
              </button>
            </div>
          </div>

          {/* User List */}
          <div className="p-sm flex w-full flex-1 flex-col items-center justify-start overflow-y-auto">
            {fetching ? (
              <div className="p-lg flex items-center justify-center">
                <DetailText className="text-on-surface-variant">Loading community...</DetailText>
              </div>
            ) : error ? (
              <div className="p-lg flex items-center justify-center">
                <DetailText className="text-on-surface-variant">Error: {error.message}</DetailText>
              </div>
            ) : userAchievements.length > 0 ? (
              <div className="gap-xs flex h-full w-full flex-wrap items-start justify-center">
                {userAchievements.map((userAchievement) => (
                  <CommunityUserItem key={userAchievement.id} user={userAchievement.user_profile} />
                ))}
              </div>
            ) : (
              <div className="p-sm border-outline-variant rounded-xs mt-sm border border-dashed">
                <BodyText className="text-on-surface-variant text-center">
                  No members yet. Complete this achievement to join the community.
                </BodyText>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-sm border-t-outline-variant gap-sm flex items-center justify-center border-t">
              <Button
                colorTheme="outline"
                onClick={handlePrevPage}
                disabled={!hasPrevPage || fetching}
                iconLeft={<ArrowRightIcon className="w-sm h-sm fill-on-surface rotate-180" />}
                className="px-xs"
              ></Button>

              <div className="gap-x-sm text-on-surface flex font-medium">
                <DetailText>{pageNumber}</DetailText>
                <DetailText>/</DetailText>
                <DetailText>{totalPages}</DetailText>
              </div>

              <Button
                colorTheme="outline"
                onClick={handleNextPage}
                disabled={!hasNextPage || fetching}
                iconRight={<ArrowRightIcon className="w-sm h-sm fill-on-surface" />}
                className="px-xs"
              ></Button>
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  )
}
